import React, { useState, useEffect, useCallback } from "react";

import sortBy from "lodash/sortBy";
import styled from "styled-components";

import Select, { SelectProps } from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';

import { Pathogen, UnitType } from "../models";
import {
  SectionWrapper,
  SecondaryHeader,
  FormCard,
  FieldGroup,
  Label
} from '../styles/global'

import { getPathogens } from "../utils/api.utils";

import { calculatePathogen } from "../utils/equation.utils";

import { checkAdmin } from '../utils/auth.utils'
import { useIsComponentMounted } from '../utils/hooks.utils'


import { PathogenForm } from './PathogenForm';

interface Props {
  currentSimulation: any;
  setSimulation: any;
  setCustomPathogen: any;
  customPathogen: any;
  timeStep?: UnitType;
  currentUnitType?: UnitType;
}

export const PathogenSettings = ({ currentSimulation, setSimulation, setCustomPathogen, customPathogen, timeStep, currentUnitType }: Props) => {
  const [pathogens, setPathogens] = useState<Pathogen[]>([]);
  const pathogensLoaded = pathogens && pathogens.length >= 1;
  const admin = checkAdmin();
  const valid = currentSimulation.pathogen_id;

  const isComponentMounted = useIsComponentMounted();

  const fetchPathogens = useCallback(() => {
    getPathogens()
      .then((result: any) => {
        if (isComponentMounted.current) {
          const payload = result as any;
          setPathogens(sortBy(payload, "created_at"));
        }
      })
      .catch((err: any) => {
        if (isComponentMounted.current) {
          setPathogens([])
        }
      });
  }, [isComponentMounted]);


  useEffect(() => {
    fetchPathogens()
  }, [fetchPathogens]);

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
      <SectionWrapper>
        <SecondaryHeader>Pathogen Configuration</SecondaryHeader>
        <FormCard style={{ maxWidth: '800px' }}>
          <FieldGroup>
            <Label>Select Base Pathogen</Label>
            <StyledSelect
              fullWidth
              id="pathogen-select"
              variant="outlined"
              MenuProps={{ getContentAnchorEl: null }}
              error={!valid}
              disabled={!pathogensLoaded || !!currentSimulation.is_modified}
              value={(pathogensLoaded && currentSimulation.pathogen_id && currentSimulation.pathogen_id !== 0) ? currentSimulation.pathogen_id : ""}
              onChange={(e) => {
                const { value }: any = e.target;
                if (value !== 0 && value !== "") {
                  setSimulation((current: any) => {
                    let base = pathogens.find(p => Number(p.id) === Number(value));
                    let newPathogen = JSON.parse(JSON.stringify(base));
                    setCustomPathogen(calculatePathogen(current, newPathogen, timeStep))
                    return {
                      ...current,
                      pathogen_id: value,
                      is_modified: false,
                      pathogen_name: base?.name || ""
                    };
                  });
                }
              }}
            >
              <MenuItem value="" disabled>Select a Pathogen</MenuItem>
              {pathogensLoaded &&
                pathogens.map((pathogen: any) => (
                  <MenuItem key={pathogen.id} value={pathogen.id}>{pathogen.name}</MenuItem>
                ))
              }
            </StyledSelect>
            {!valid && <ErrorText>You must select a Pathogen to continue</ErrorText>}
          </FieldGroup>

          {admin && (
            <FieldGroup style={{ marginTop: '32px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={!pathogensLoaded || !currentSimulation.pathogen_id}
                    checked={!!currentSimulation.is_modified}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setSimulation((current: any) => {
                        let base = pathogens.find(p => Number(p.id) === Number(current.pathogen_id));
                        if (!base) return current;

                        let newPathogen = JSON.parse(JSON.stringify(base));
                        if (isChecked) {
                          setCustomPathogen(calculatePathogen(current, newPathogen, timeStep))
                        }

                        return {
                          ...current,
                          is_modified: isChecked,
                          pathogen_name: isChecked ? `${base.name} (Modified)` : base.name
                        };
                      });
                    }}
                    color="primary"
                  />
                }
                label={
                  <div style={{ marginLeft: '8px' }}>
                    <div style={{ fontWeight: 700, color: '#2d3748', fontSize: '14px' }}>Override Simulation Parameters</div>
                    <div style={{ fontSize: '12px', color: '#718096' }}>Enable custom adjustments for this specific simulation instance</div>
                  </div>
                }
              />
            </FieldGroup>
          )}

          {!!currentSimulation.is_modified && (
            <div style={{ marginTop: '32px', borderTop: '1px solid #f1f5f9', paddingTop: '32px' }}>
              <FieldGroup>
                <Label>Modified Pathogen Name</Label>
                <TextField
                  fullWidth
                  variant="outlined"
                  required
                  value={currentSimulation.pathogen_name}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSimulation((current: any) => ({ ...current, pathogen_name: val }));
                  }}
                />
              </FieldGroup>

              <div style={{ marginTop: '24px' }}>
                <PathogenForm
                  pathogen={customPathogen}
                  simulation={currentSimulation}
                  setPathogen={setCustomPathogen}
                  timeStep={timeStep}
                  currentUnitType={currentUnitType}
                />
              </div>
            </div>
          )}
        </FormCard>
      </SectionWrapper>
    </div>
  );
};

const ErrorText = styled.div`
  display: block;
  font-weight: 700;
  font-size: 12px;
  margin-top: 8px;
  color: #e53e3e;
`;

const StyledSelect = styled((props: SelectProps) => <Select {...props} />)`
  && {
    .MuiSelect-select {
      background: white;
    }
  }
`;
